import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarHeart, MessageCircle, ArrowRight, Stethoscope, Baby, ShieldCheck,
  HeartPulse, Microscope, Flower2, Sparkles, Star, Clock,
} from "lucide-react";
import { SITE, waLink } from "../lib/site";
import { ColposcopiaInfoDialog } from "../components/ColposcopiaInfoDialog";
import WhatsAppCTABlock from "../components/WhatsAppCTABlock";
import WhyChooseSection from "../components/WhyChooseSection";
import BenefitsSection from "../components/BenefitsSection";
import ModeloAtencionTimeline from "../components/ModeloAtencionTimeline";
import BlogPreview from "../components/BlogPreview";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const services = [
  { icon: HeartPulse, title: "Atención personalizada", text: "Tiempo, escucha y trato humano en cada consulta.", href: "/quien-soy" },
  { icon: Baby, title: "Control prenatal", text: "Seguimiento integral para mamá y bebé.", href: "/control-prenatal" },
  { icon: Flower2, title: "Salud ginecológica", text: "Prevención y cuidado en cada etapa de tu vida.", href: "/salud-ginecologica" },
  { icon: Microscope, title: "Colposcopia y Papanicolaou", text: "Diagnóstico oportuno que salva vidas.", modal: "colposcopia" },
  { icon: Sparkles, title: "Menopausia y climaterio", text: "Acompañamiento en una etapa natural y única.", href: "/salud-ginecologica#menopausia" },
  { icon: Clock, title: "Agenda rápida", text: "Confirmación inmediata por WhatsApp.", href: SITE.bookingUrl, external: true },
];

const testimonials = [
  { name: "Ana L.", text: "Una experiencia muy humana, me explicó cada paso del embarazo con calma." },
  { name: "María P.", text: "Profesional, cálido y siempre disponible. Confío plenamente en su criterio." },
  { name: "Laura R.", text: "Recomiendo al Dr. Franco al 100%. Me sentí en las mejores manos." },
];

export default function Inicio() {
  return (
    <div data-testid="page-inicio">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{
            background:
              "radial-gradient(800px 500px at 75% 20%, rgba(79,163,255,0.25), transparent 60%), radial-gradient(700px 500px at 10% 80%, rgba(30,90,168,0.10), transparent 60%), linear-gradient(180deg, #F8FBFF 0%, #EEF3F9 100%)",
          }}
        />
        <div className="mx-auto max-w-7xl px-6 md:px-12 pt-10 md:pt-16 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-brand-medical mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-bright animate-pulse" />
              Ginecología · Obstetricia
            </span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-brand-deep tracking-tight">
              Tu salud femenina <br />
              <em className="not-italic text-gradient-blue">en manos profesionales</em>
            </h1>
            <p className="mt-6 max-w-xl text-lg md:text-xl text-brand-deep/70 font-light leading-relaxed">
              Ginecología y Obstetricia con enfoque en <strong className="text-brand-deep font-medium">calidad, seguridad y experiencia de la paciente</strong>.
            </p>
            <p className="mt-3 max-w-xl text-sm text-brand-medical/85 font-medium tracking-wide">
              Especialista en Ginecología y Obstetricia · Maestro en Administración Hospitalaria
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-agendar-btn"
                className="inline-flex items-center gap-2 rounded-full bg-brand-medical hover:bg-brand-deep text-white px-7 py-3.5 font-medium tracking-wide transition-all duration-300 premium-shadow hover:scale-[1.02]"
              >
                <CalendarHeart className="w-4 h-4" strokeWidth={1.5} />
                Agendar cita
              </a>
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-brand-medical/40 hover:ring-brand-medical text-brand-medical hover:bg-white px-7 py-3.5 font-medium tracking-wide transition-all duration-300"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                WhatsApp
              </a>
              <a
                href="#servicios"
                data-testid="hero-servicios-btn"
                className="inline-flex items-center gap-2 text-brand-deep/70 hover:text-brand-medical px-3 py-2 font-medium tracking-wide transition-colors"
              >
                Conocer servicios <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "+15", s: "Años de experiencia" },
                { k: "+2.5k", s: "Pacientes atendidas" },
                { k: "98%", s: "Satisfacción" },
              ].map((s) => (
                <div key={s.s} className="border-l border-brand-silver pl-4">
                  <p className="font-serif text-3xl text-brand-deep">{s.k}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-brand-deep/60 mt-1">{s.s}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -inset-4 rounded-[3rem] bg-gradient-to-br from-brand-bright/30 to-brand-silver/20 blur-2xl" />
            <div className="relative rounded-[2.5rem] overflow-hidden hairline premium-shadow bg-white">
              <img
                src={SITE.images.heroDoctor}
                alt="Atención médica profesional"
                className="w-full h-[520px] md:h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/30 via-transparent to-transparent" />
            </div>

            {/* Floating card */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="absolute -bottom-6 -left-4 md:-left-10 glass-strong rounded-2xl px-5 py-4 premium-shadow max-w-[230px]"
            >
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4 text-brand-medical" strokeWidth={1.5} />
                <p className="text-xs uppercase tracking-[0.18em] text-brand-medical">Cédula 11533442</p>
              </div>
              <p className="font-serif text-sm text-brand-deep leading-snug">
                Especialista certificado en Ginecología y Obstetricia.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: -18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.6 }}
              className="absolute -top-4 -right-2 md:-right-6 glass-strong rounded-2xl px-4 py-3 premium-shadow"
            >
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-brand-bright text-brand-bright" />
                ))}
              </div>
              <p className="text-xs text-brand-deep/70 mt-1">Pacientes satisfechas</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES (bento) */}
      <section id="servicios" className="mx-auto max-w-7xl px-6 md:px-12 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-medical mb-3">Servicios</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-deep leading-[1.05]">
              Cuidado integral en <em className="not-italic text-gradient-blue">cada etapa</em> de tu vida.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 self-end">
            <p className="text-brand-deep/70 font-light text-lg leading-relaxed">
              Desde tu primer chequeo hasta el seguimiento de tu embarazo y los cambios
              de la menopausia — un acompañamiento médico cercano, claro y profesional.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => {
            const isAccent = i === 1;
            const cardCls = `group relative rounded-3xl p-8 hairline overflow-hidden transition-all duration-500 text-left w-full ${
              isAccent
                ? "bg-gradient-to-br from-brand-deep to-brand-medical text-white lg:row-span-1"
                : "bg-white premium-shadow hover:premium-shadow-hover"
            }`;
            const content = (
              <>
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 ${
                  isAccent ? "bg-white/15 ring-1 ring-white/20" : "bg-brand-cloud ring-1 ring-brand-silver/60"
                }`}>
                  <s.icon className={`w-5 h-5 ${isAccent ? "text-white" : "text-brand-medical"}`} strokeWidth={1.4} />
                </div>
                <h3 className={`font-serif text-2xl mb-2 ${isAccent ? "text-white" : "text-brand-deep"}`}>
                  {s.title}
                </h3>
                <p className={`font-light leading-relaxed ${isAccent ? "text-white/80" : "text-brand-deep/70"}`}>
                  {s.text}
                </p>
                <div className={`mt-6 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] ${
                  isAccent ? "text-brand-silver" : "text-brand-medical"
                }`}>
                  Saber más <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </>
            );

            const motionWrapProps = {
              initial: { opacity: 0, y: 24 },
              whileInView: { opacity: 1, y: 0 },
              viewport: { once: true, margin: "-60px" },
              transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
              whileHover: { y: -6 },
            };

            if (s.modal === "colposcopia") {
              return (
                <ColposcopiaInfoDialog
                  key={s.title}
                  trigger={
                    <motion.button
                      type="button"
                      {...motionWrapProps}
                      data-testid={`service-${i}`}
                      className={cardCls + " cursor-pointer"}
                    >
                      {content}
                    </motion.button>
                  }
                />
              );
            }

            return (
              <motion.div key={s.title} {...motionWrapProps} data-testid={`service-${i}`} className={cardCls}>
                {s.external ? (
                  <a href={s.href} target="_blank" rel="noopener noreferrer" className="absolute inset-0" aria-label={s.title} />
                ) : (
                  <Link to={s.href} className="absolute inset-0" aria-label={s.title} />
                )}
                <div className="relative pointer-events-none">{content}</div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* WHY CHOOSE */}
      <WhyChooseSection />

      {/* BENEFITS */}
      <BenefitsSection />

      {/* TESTIMONIALS */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white to-brand-cloud/60" />
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex flex-col items-center text-center mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-medical mb-3">Testimonios</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-deep max-w-3xl leading-tight">
              La voz de quienes han confiado en mi cuidado.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white rounded-3xl p-8 hairline premium-shadow"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5 fill-brand-bright text-brand-bright" />
                  ))}
                </div>
                <p className="font-serif text-xl text-brand-deep leading-snug mb-5">
                  “{t.text}”
                </p>
                <p className="text-xs uppercase tracking-[0.2em] text-brand-medical">{t.name}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <a
              href={SITE.googleReviews}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="group inline-flex items-center gap-3 rounded-full bg-white hairline premium-shadow hover:premium-shadow-hover px-6 py-3 transition-all"
            >
              <span className="flex items-center -space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-brand-bright text-brand-bright" />
                ))}
              </span>
              <span className="text-sm font-medium text-brand-deep">
                Ver opiniones reales en <span className="text-brand-medical">Google</span>
              </span>
              <ArrowRight className="w-4 h-4 text-brand-medical group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* WhatsApp CTAs */}
      <WhatsAppCTABlock highlight="cta-consulta" />

      {/* MODELO DE ATENCIÓN */}
      <ModeloAtencionTimeline />

      {/* BLOG / EDUCACIÓN */}
      <BlogPreview />

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 py-16 md:py-24">
        <div className="relative rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-brand-deep via-brand-medical to-brand-bright p-10 md:p-16 grain">
          <div className="absolute -bottom-32 -right-32 w-[460px] h-[460px] rounded-full bg-white/10 blur-3xl" />
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-brand-silver mb-3">
                <Stethoscope className="inline w-3.5 h-3.5 mr-1" /> Agenda rápida
              </p>
              <h3 className="font-serif text-4xl md:text-5xl text-white leading-tight">
                Será un gusto acompañarte en el cuidado de tu salud.
              </h3>
            </div>
            <div className="flex flex-col sm:flex-row md:justify-end gap-3">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-agendar"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand-deep hover:bg-brand-silver px-7 py-3.5 font-medium tracking-wide transition-colors"
              >
                <CalendarHeart className="w-4 h-4" />
                Agendar cita
              </a>
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp"
                className="inline-flex items-center justify-center gap-2 rounded-full ring-1 ring-white/40 text-white hover:bg-white/10 px-7 py-3.5 font-medium tracking-wide transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
